import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Link2, Copy, Check, Loader2, Users, Calendar,
  ExternalLink, RefreshCw, Globe, Power, PowerOff, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { generatePublicLink, getPublicLink, disablePublicLink } from '../services/api';

export function PublicLinkModal({ isOpen, onClose, jobId, jobTitle, hasQuestions }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expiryDays, setExpiryDays] = useState('none');
  const [resumeRequired, setResumeRequired] = useState(true);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchLink();
    }
  }, [isOpen, jobId]);

  const fetchLink = async () => {
    try {
      setLoading(true);
      const data = await getPublicLink(jobId);
      if (data.exists) {
        setLinkData(data);
        setResumeRequired(data.resume_required ?? true);
      } else {
        setLinkData(null);
      }
    } catch {
      setLinkData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const days = expiryDays === 'none' ? null : parseInt(expiryDays);
      const data = await generatePublicLink(jobId, days, resumeRequired);
      setLinkData({ exists: true, ...data, applicant_count: 0 });
      toast.success('Public test link generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate link');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!linkData) return;
    const fullUrl = `${(process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || window.location.origin}${linkData.url}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDisable = async () => {
    try {
      setDisabling(true);
      await disablePublicLink(jobId);
      setLinkData(prev => prev ? { ...prev, enabled: false } : null);
      toast.success('Public link disabled');
    } catch {
      toast.error('Failed to disable link');
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setGenerating(true);
      const days = expiryDays === 'none' ? null : parseInt(expiryDays);
      const data = await generatePublicLink(jobId, days, resumeRequired);
      setLinkData({ exists: true, ...data, applicant_count: 0 });
      toast.success('New public test link generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate link');
    } finally {
      setGenerating(false);
    }
  };

  const fullUrl = linkData?.url ? `${(process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || window.location.origin}${linkData.url}` : '';
  const isExpired = linkData?.expires_at && new Date(linkData.expires_at) < new Date();
  const isActive = linkData?.enabled && !isExpired;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px]" data-testid="public-link-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Globe className="h-5 w-5 text-indigo-500" />
            Public Test Link
          </DialogTitle>
          <DialogDescription>
            Share this link with candidates to let them self-register and take the interview
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !hasQuestions ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
              <Link2 className="h-5 w-5 text-amber-500" />
            </div>
            <p className="text-sm font-medium mb-1">Interview Questions Required</p>
            <p className="text-xs text-muted-foreground">Generate interview questions first before creating a public test link.</p>
          </div>
        ) : linkData && linkData.exists ? (
          <div className="space-y-5 py-2">
            {/* Status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isActive ? (
                  <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1">
                    <Power className="h-3 w-3" /> Active
                  </Badge>
                ) : isExpired ? (
                  <Badge variant="destructive" className="gap-1">
                    <Calendar className="h-3 w-3" /> Expired
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1">
                    <PowerOff className="h-3 w-3" /> Disabled
                  </Badge>
                )}
                <Badge variant="outline" className="gap-1 text-xs">
                  <Users className="h-3 w-3" /> {linkData.applicant_count || 0} applicants
                </Badge>
              </div>
            </div>

            {/* Link URL */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Share this URL</Label>
              <div className="flex gap-2">
                <Input
                  value={fullUrl}
                  readOnly
                  className="text-xs font-mono"
                  data-testid="public-link-url-input"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleCopy}
                  className="shrink-0"
                  data-testid="public-link-copy-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => window.open(fullUrl, '_blank')}
                  className="shrink-0"
                  data-testid="public-link-open-btn"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Expiry info */}
            {linkData.expires_at && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {isExpired
                  ? `Expired on ${new Date(linkData.expires_at).toLocaleDateString()}`
                  : `Expires on ${new Date(linkData.expires_at).toLocaleDateString()}`
                }
              </p>
            )}
            {!linkData.expires_at && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" /> No expiry set (active until disabled)
              </p>
            )}

            {/* Resume requirement indicator */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <FileText className="h-3 w-3" />
              Resume: <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid="resume-required-badge">
                {linkData.resume_required ? 'Required' : 'Optional'}
              </Badge>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              {isActive && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisable}
                  disabled={disabling}
                  className="gap-1.5 text-red-500 border-red-500/20 hover:bg-red-500/5"
                  data-testid="public-link-disable-btn"
                >
                  {disabling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PowerOff className="h-3.5 w-3.5" />}
                  Disable Link
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegenerate}
                disabled={generating}
                className="gap-1.5"
                data-testid="public-link-regenerate-btn"
              >
                {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                New Link
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-indigo-500/10 flex items-center justify-center mx-auto mb-3">
                <Globe className="h-6 w-6 text-indigo-500" />
              </div>
              <p className="text-sm font-medium mb-1">No Public Link Yet</p>
              <p className="text-xs text-muted-foreground max-w-[320px] mx-auto">
                Generate a public link that anyone can use to self-register, upload their resume, and take the interview test.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Link Expiry</Label>
              <Select value={expiryDays} onValueChange={setExpiryDays}>
                <SelectTrigger data-testid="public-link-expiry-select">
                  <SelectValue placeholder="Select expiry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No expiry (manual control)</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border" data-testid="resume-required-toggle-row">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Resume Upload</p>
                  <p className="text-[11px] text-muted-foreground">
                    {resumeRequired ? 'Candidates must upload their resume' : 'Resume is optional for candidates'}
                  </p>
                </div>
              </div>
              <Switch
                checked={resumeRequired}
                onCheckedChange={setResumeRequired}
                data-testid="resume-required-toggle"
              />
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full h-11 gap-2"
              data-testid="public-link-generate-btn"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <><Link2 className="h-4 w-4" /> Generate Public Test Link</>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
